/**
 * Helper para exportar datos tabulares a un archivo Excel (.xlsx) real.
 *
 * Sustituye a los antiguos `Blob([csv], {type:"text/csv"})` que tenían
 * problemas de separador con Excel en locales europeos (donde el separador
 * de listas es `;` y CSV con comas queda todo en una sola columna).
 */
import * as XLSX from "xlsx";

export interface XlsxColumn<T = Record<string, unknown>> {
  /** Encabezado visible en la hoja */
  header: string;
  /** Clave en el objeto o función accesora */
  key: keyof T | ((row: T) => unknown);
  /** Ancho de columna (en caracteres aprox). Default: auto según header. */
  width?: number;
}

export interface ExportToXlsxOptions<T = Record<string, unknown>> {
  /** Filas a exportar (objetos planos) */
  rows: T[];
  /** Definición de columnas. Si se omite, se usan las claves de la primera fila. */
  columns?: XlsxColumn<T>[];
  /** Nombre del archivo (sin extensión). Se agrega `.xlsx` automáticamente. */
  filename: string;
  /** Nombre de la hoja. Default: "Datos". Máx 31 chars (limitación Excel). */
  sheetName?: string;
}

/**
 * Sanea el nombre de hoja: Excel no permite ciertos caracteres y máx 31 chars.
 */
function sanitizeSheetName(name: string): string {
  return name.replace(/[\\/?*[\]:]/g, "").slice(0, 31) || "Datos";
}

/**
 * Genera y descarga un archivo .xlsx en el navegador.
 */
export function exportToXlsx<T>(options: ExportToXlsxOptions<T>): void {
  const { rows, filename, sheetName = "Datos" } = options;

  // Si no hay columnas definidas, derivar de la primera fila.
  const columns: XlsxColumn<T>[] =
    options.columns ??
    (rows.length > 0
      ? Object.keys(rows[0] as Record<string, unknown>).map((k) => ({
          header: k,
          key: k as keyof T,
        }))
      : []);

  // Convertir filas a un array de arrays con header en la primera fila.
  const headerRow = columns.map((c) => c.header);
  const dataRows = rows.map((row) =>
    columns.map((c) => {
      const value =
        typeof c.key === "function"
          ? c.key(row)
          : (row[c.key as keyof T] as unknown);
      if (value === null || value === undefined) return "";
      if (value instanceof Date) return value;
      return value;
    }),
  );

  const aoa: unknown[][] = [headerRow, ...dataRows];
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  // Anchos de columna: usar el width explícito o calcular según el contenido.
  worksheet["!cols"] = columns.map((c, i) => {
    if (c.width) return { wch: c.width };
    const headerLen = c.header.length;
    let maxCell = headerLen;
    for (const row of dataRows) {
      const v = row[i];
      const len = v instanceof Date ? 10 : v == null ? 0 : String(v).length;
      if (len > maxCell) maxCell = len;
    }
    return { wch: Math.min(Math.max(maxCell + 2, 10), 60) };
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    sanitizeSheetName(sheetName),
  );

  XLSX.writeFile(workbook, `${filename}.xlsx`, {
    bookType: "xlsx",
    compression: true,
  });
}

/**
 * Variante para exportar varias hojas en un mismo archivo.
 */
export interface XlsxSheet<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  name: string;
  rows: T[];
  columns?: XlsxColumn<T>[];
}

export function exportMultiSheetXlsx(
  sheets: XlsxSheet[],
  filename: string,
): void {
  const workbook = XLSX.utils.book_new();
  for (const sheet of sheets) {
    const columns: XlsxColumn[] =
      sheet.columns ??
      (sheet.rows.length > 0
        ? Object.keys(sheet.rows[0]).map((k) => ({ header: k, key: k }))
        : []);
    const headerRow = columns.map((c) => c.header);
    const dataRows = sheet.rows.map((row) =>
      columns.map((c) => {
        const value =
          typeof c.key === "function"
            ? c.key(row)
            : (row[c.key as string] as unknown);
        if (value === null || value === undefined) return "";
        return value;
      }),
    );
    const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
    ws["!cols"] = columns.map((c) => ({
      wch: c.width ?? Math.min(Math.max(c.header.length + 2, 10), 60),
    }));
    XLSX.utils.book_append_sheet(workbook, ws, sanitizeSheetName(sheet.name));
  }
  XLSX.writeFile(workbook, `${filename}.xlsx`, {
    bookType: "xlsx",
    compression: true,
  });
}

/**
 * Exporta un array bidimensional crudo (cabecera + filas mixtas) a una
 * sola hoja. Útil para tablas con totales o secciones heterogéneas donde
 * no aplica el modelo de columnas tipadas.
 */
export interface ExportAoaOptions {
  aoa: unknown[][];
  filename: string;
  sheetName?: string;
  /** Anchos opcionales por columna (en caracteres). */
  colWidths?: number[];
}

export function exportAoaToXlsx(options: ExportAoaOptions): void {
  const { aoa, filename, sheetName = "Datos", colWidths } = options;
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  if (colWidths) {
    ws["!cols"] = colWidths.map((w) => ({ wch: w }));
  } else if (aoa.length > 0) {
    const maxCols = Math.max(...aoa.map((r) => r.length));
    const cols: { wch: number }[] = [];
    for (let i = 0; i < maxCols; i++) {
      let max = 10;
      for (const row of aoa) {
        const v = row[i];
        const len = v == null ? 0 : String(v).length;
        if (len > max) max = len;
      }
      cols.push({ wch: Math.min(max + 2, 60) });
    }
    ws["!cols"] = cols;
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(sheetName));
  XLSX.writeFile(wb, `${filename}.xlsx`, {
    bookType: "xlsx",
    compression: true,
  });
}
